import {
  ClassificationRule,
  DocumentValidationCheck
} from 'generated-graphql/types';
import { DateRangeUnit } from '../constants';
import {
  parseValidationSettings,
  RelativeDateRange
} from './validationSettings';

const UNIT_SINGULAR: Record<DateRangeUnit, string> = {
  DAYS: 'day',
  MONTHS: 'month',
  YEARS: 'year'
};

const formatDateRange = ({ value, unit }: RelativeDateRange): string => {
  const noun = UNIT_SINGULAR[unit];
  return `${value} ${noun}${value === 1 ? '' : 's'}`;
};

/**
 * Builds the human-readable summary line rendered under each DocumentType
 * name in the list. Combines the classification-rule fields ("Must contain
 * X and Y") with the validation checks ("Check owner's name matches, expiry,
 * document is valid for at least 3 months.").
 */
export const formatValidationSummary = ({
  classificationRules,
  validation,
  validationSettings
}: {
  classificationRules:
    | readonly Pick<ClassificationRule, 'value'>[]
    | null
    | undefined;
  validation: readonly DocumentValidationCheck[] | null | undefined;
  validationSettings: string | null | undefined;
}): string => {
  const contains = (classificationRules ?? [])
    .map((rule) => rule.value?.trim())
    .filter((v): v is string => Boolean(v));
  const checks = new Set<DocumentValidationCheck>(validation ?? []);
  const settings = parseValidationSettings(validationSettings);

  const sentences: string[] = [];

  if (contains.length) {
    sentences.push(`Must contain ${joinEnglish(contains).toLowerCase()}.`);
  }

  const checkFragments: string[] = [];
  if (checks.has(DocumentValidationCheck.CheckNameMatch)) {
    checkFragments.push("owner's name matches");
  }
  if (checks.has(DocumentValidationCheck.CheckDocumentCornerInclusion)) {
    checkFragments.push('all corners are visible');
  }
  if (checks.has(DocumentValidationCheck.CheckExpirationDate)) {
    checkFragments.push(
      settings.validFor
        ? `document is valid for at least ${formatDateRange(settings.validFor)}`
        : 'document has not expired'
    );
  }
  if (checks.has(DocumentValidationCheck.CheckIssueDate)) {
    checkFragments.push(
      settings.issuedAfter
        ? `document was issued in the last ${formatDateRange(
            settings.issuedAfter
          )}`
        : 'document was issued'
    );
  }

  if (checkFragments.length) {
    sentences.push(`Check ${joinEnglish(checkFragments)}.`);
  }

  return sentences.join(' ');
};

const joinEnglish = (parts: readonly string[]): string => {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
};
